import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { merchantAuthController } from '../controllers/authController';
import Loader from './Loader';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, checkAuthStatus } = useAuth();
  const { profile, setProfile } = useProfile();
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(isAuthenticated);
  const location = useLocation();

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const isValid = await checkAuthStatus();
        setAuthenticated(isValid);

        // Always fetch fresh profile data from API if authenticated
        if (isValid) {
          try {
            const profileRes = await merchantAuthController.getProfileInfo();
            const profileData = profileRes.data?.data || profileRes.data;
            setProfile(profileData);
          } catch (profileErr) {
            console.error('Failed to fetch profile:', profileErr);
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        setAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };

    verifyAuth();
  }, [checkAuthStatus, setProfile]);

  // Show loading state while checking authentication
  if (loading || checking) {
    return <Loader />;
  }

  // Redirect to login if not authenticated
  if (!authenticated) {
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children or outlet
  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute; 